import { Location } from "@remix-run/router/history";
import * as Sentry from "@sentry/browser";
import { appQueryClient } from "app/AppQueryClient";
import { appStytchClient } from "app/AppStytchClient";
import { IAuthTokens, setAuthTokens } from "axios-jwt";
import { STORAGE_KEY } from "axios-jwt/dist/src/StorageKey";
import {
  isStytchSessionExpired,
  isStytchSessionExtended,
} from "features/auth/StytchUtils";
import { TokenObtainPairResponse } from "gen/model/tokenObtainPairResponse";
import log from "loglevel";
import LogRocket from "logrocket";
import { useCallback, useEffect, useRef } from "react";
import { Path, useLocation, useNavigate } from "react-router-dom";
import { AppConfig } from "shared/config/AppConfig";
import { rootRoute } from "shared/navigation/Routes";
import {
  clearLocalStorage,
  makeJsonItemLocalStorage,
  makeJsonItemSessionStorage,
} from "shared/storage/StorageUtils";
import { logRocketIdentify } from "shared/utils/LogRocketUtils";
import { useLocalStorage } from "usehooks-ts";

const STORAGE_KEY_SELECTED_CUSTOMER_ID = "selectedCustomerId";
const INTERNAL_STORAGE_KEY_SELECTED_CUSTOMER_ID = "internalSelectedCustomerId";
const SESSION_KEY_PATH_AFTER_LOGIN = "authReturnTo";

export const authTokenStorage = makeJsonItemLocalStorage<IAuthTokens | null>(
  STORAGE_KEY,
  null,
);

export const locationAfterLoginStorage = makeJsonItemSessionStorage<
  Location | undefined
>(SESSION_KEY_PATH_AFTER_LOGIN, undefined);

export const selectedCustomerIdStorage = makeJsonItemLocalStorage<
  string | undefined
>(STORAGE_KEY_SELECTED_CUSTOMER_ID, undefined);

export const internalSelectedCustomerIdStorage = makeJsonItemLocalStorage<
  string | undefined
>(INTERNAL_STORAGE_KEY_SELECTED_CUSTOMER_ID, undefined);

export interface UserStorageData {
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  intercomUserHash: string | undefined;
}

export const userStorage = makeJsonItemLocalStorage<
  UserStorageData | undefined
>("user", undefined);

export const signIn = async ({
  access,
  refresh,
  clients,
  user_id,
  email,
  first_name,
  last_name,
  intercom_user_hash,
}: TokenObtainPairResponse) => {
  await setAuthTokens({
    accessToken: access,
    refreshToken: refresh,
  });
  userStorage.set({
    userId: user_id,
    email,
    firstName: first_name,
    lastName: last_name,
    intercomUserHash: intercom_user_hash,
  });
  if (!selectedCustomerIdStorage.get() && clients.length > 0) {
    selectedCustomerIdStorage.set(clients[0].id!);
  }
  if (AppConfig.sentry) {
    Sentry.setUser({ id: user_id, email });
  }
  if (AppConfig.logRocket) {
    logRocketIdentify(user_id, email, first_name, last_name);
  }
  window.dispatchEvent(new Event("local-storage"));
};

export const signOut = async (force: boolean) => {
  // axios-jwt provides clearAuthData() but that won't clear everything else
  clearLocalStorage();

  appQueryClient.clear();

  if (AppConfig.logRocket) {
    try {
      LogRocket.startNewSession();
    } catch (e) {
      log.error("Error starting new logrocket session", e);
    }
  }

  if (AppConfig.stytch) {
    const session = appStytchClient.session.getSync();
    const shouldRevokeStytchSession = Boolean(
      session &&
        (force ||
          // Don't revoke a valid stytch session if "remember for 30 days"
          // was selected during sign in ("extended" session).
          !isStytchSessionExtended(session) ||
          isStytchSessionExpired(session)),
    );
    if (shouldRevokeStytchSession) {
      try {
        await appStytchClient.session.revoke({ forceClear: true });
      } catch (e) {
        log.error("Error revoking stytch session", e);
      }
    }
  }
};

export const useIsLoggedIn = () => {
  // Equivalent to isLoggedIn() from axios-jwt
  const [rawTokens] = useLocalStorage(STORAGE_KEY, null);
  return !!rawTokens;
};

export const useGetPathAfterLogin = () => {
  const location = useLocation();

  const getPathAfterLogin = () => {
    const pathFromState = (
      location.state as
        | {
            from?: Path;
          }
        | undefined
    )?.from;
    const pathFromSession = locationAfterLoginStorage.get();
    return (
      pathFromState?.pathname ?? pathFromSession?.pathname ?? rootRoute({}).$
    );
  };

  const pathRef = useRef<string>(getPathAfterLogin());
  const didCallGetterRef = useRef(false);

  useEffect(
    () => () => {
      if (didCallGetterRef.current) {
        sessionStorage.removeItem(SESSION_KEY_PATH_AFTER_LOGIN);
      }
    },
    [],
  );

  return useCallback(() => {
    didCallGetterRef.current = true;
    return pathRef.current;
  }, []);
};

export const useAuthContinue = () => {
  const navigate = useNavigate();
  return useCallback(() => {
    navigate("/auth/continue", { replace: true });
  }, [navigate]);
};

export const useAuthCancel = () => {
  const navigate = useNavigate();
  return useCallback(async () => {
    await signOut(true);
    navigate("/auth/login", { replace: true });
  }, [navigate]);
};
